import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Observable, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@iw-shared/store/state/app.state';
import { EnvironmentService } from '@iw-shared/core/services/environment.service';
import { CookieService } from 'ngx-cookie-service';
import {
  ApiUserTokensDeleteResponse,
  ApiUserTokensRefreshResponse,
  ApiUserTokensSetResponse
} from "@iw-shared/api/user-tokens";
import {
  ApiUserTokensAccountDeleteRequest, ApiUserTokensAccountDeleteResponse,
  ApiUserTokensAccountRefreshResponse,
  ApiUserTokensAccountSetResponse
} from "@iw-shared/api/user-tokens-account";
import {ApiUsersGetRequest, ApiUsersGetResponse} from "@iw-shared/api/users";
import {ApiUsersAccountGetRequest, ApiUsersAccountGetResponse} from "@iw-shared/api/user-account";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private httpWithoutInterceptor: HttpClient;

  // Wir erstellen "Signals", die den Store spiegeln.
  // Das erlaubt uns, jederzeit synchron den aktuellen Wert zu lesen.
  private authState = signal<any>(null);

  constructor(
    private http: HttpClient,
    private handler: HttpBackend,
    private store: Store<AppState>,
    private environmentService: EnvironmentService,
    private cookieService: CookieService
  ) {
    this.httpWithoutInterceptor = new HttpClient(this.handler);

    // Initialisierung: Wir abonnieren den Store einmalig, um das Signal aktuell zu halten
    this.store.select(state => state.storage).subscribe(storage => {
      this.authState.set(storage);
    });
  }

  // --- GETTER (SYNCHRON via Signals) ---

  private get storage() {
    return this.authState();
  }

  isInjectionToken(): boolean {
    return !!this.storage?.tokens?.access?.injectionToken;
  }

  getInjectionToken(): string {
    return this.storage?.tokens?.access?.injectionToken || '';
  }

  getPayloadFromInjectionToken(): { userId: string, exp: number } {
    const token = this.getInjectionToken();
    if (!token) return { userId: '', exp: 0 };
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      console.error("Fehler beim Dekodieren des Injection-Tokens", e);
      return { userId: '', exp: 0 };
    }
  }

  getCreatedAccessTimestampInSeconds(): number {
    return this.storage?.tokens?.access?.createdTimestampInSeconds || 0;
  }

  getAccessExpiredInSeconds(): number {
    return this.storage?.tokens?.access?.expiredIn || 0;
  }

  getCreatedRefreshTimestampInSeconds(): number {
    return this.storage?.tokens?.refresh?.createdTimestampInSeconds || 0;
  }

  getExpiredRefreshInSeconds(): number {
    return this.storage?.tokens?.refresh?.expiredIn || 0;
  }

  // --- COOKIE MANAGEMENT ---

  setAccessTokenCookie() {
    this.cookieService.set('isAccessToken', 'true', 0, '/');
  }

  deleteAccessTokenCookie() {
    this.cookieService.delete('isAccessToken');
  }

  isAccessTokenCookie(): boolean {
    return this.cookieService.get('isAccessToken') === 'true';
  }

  // --- LOGIK PRÜFUNGEN ---

  isAccessToken(): boolean {
    return !!this.storage?.tokens?.access;
  }

  isAccessTokenExpired(minutesBefore: number): boolean {
    const created = this.getCreatedAccessTimestampInSeconds();
    const expires = this.getAccessExpiredInSeconds();
    if (created === 0 || expires === 0) return true;

    const nowInSeconds = Date.now() / 1000;
    // Gibt true zurück, wenn (Gültigkeit - Puffer) <= Jetzt
    return (created + expires - (minutesBefore * 60)) <= nowInSeconds;
  }

  isRefreshToken(): boolean {
    return !!this.storage?.tokens?.refresh;
  }

  isRefreshTokenExpired(): boolean {
    const created = this.getCreatedRefreshTimestampInSeconds();
    const expires = this.getExpiredRefreshInSeconds();
    if (created === 0 || expires === 0) return true;

    const nowInSeconds = Date.now() / 1000;
    return (created + expires) <= nowInSeconds;
  }

  isAuthentication(): boolean {
    return (this.isAccessToken() && !this.isAccessTokenExpired(5)) ||
      (this.isRefreshToken() && !this.isRefreshTokenExpired());
  }

  isStorageUserId(): boolean {
    return !!this.storage?.userId;
  }

  // --- API CALLS ---

  login(username: string, password: string): Observable<ApiUserTokensSetResponse> {
    const body = {
      id: 'login',
      command: 'userTokens/set',
      params: { data: { username, password } }
    };
    return this.httpWithoutInterceptor.post<ApiUserTokensSetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
  }

  loginAccount(username: string, password: string): Observable<ApiUserTokensAccountSetResponse> {
    const body = {
      id: 'login',
      command: 'accountBackendUserTokens/set',
      params: { data: { username, password } }
    };
    return this.httpWithoutInterceptor.post<ApiUserTokensAccountSetResponse>(this.environmentService.environment.urlMainApi, JSON.stringify(body));
  }

  refreshToken(): Observable<ApiUserTokensRefreshResponse> {
    const body = { id: 'refreshToken', command: 'userTokens/refresh' };
    return this.httpWithoutInterceptor.post<ApiUserTokensRefreshResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
  }

  refreshTokenAccount(): Observable<ApiUserTokensAccountRefreshResponse> {
    const body = { id: 'refreshToken', command: 'accountBackendUserTokens/refresh' };
    return this.httpWithoutInterceptor.post<ApiUserTokensAccountRefreshResponse>(this.environmentService.environment.urlMainApi, JSON.stringify(body));
  }

  getUsers(userId: string): Observable<ApiUsersGetResponse> {
    const body: ApiUsersGetRequest = {
      id: 'getUsersAdmin',
      command: 'users/get',
      params:{
        filters: [{
          fields: ['id'],
          values: [userId]
        }],
        users:{}
      }
    };
    return this.http.post<ApiUsersGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
  }

  getUsersAccount(userId: string): Observable<ApiUsersAccountGetResponse> {
    const body: ApiUsersAccountGetRequest = {
      id: 'getUsersAdmin',
      command: 'accountBackendUsers/get',
      params:{
        filters: [{
          fields: ['id'],
          values: [userId]
        }]

      }
    };
    return this.http.post<ApiUsersAccountGetResponse>(this.environmentService.environment.urlMainApi, JSON.stringify(body));
  }

  logout(): Observable<ApiUserTokensDeleteResponse> {
    const body = { id: 'delete', command: 'userTokens/delete' };
    return this.httpWithoutInterceptor.post<ApiUserTokensDeleteResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
  }
  logoutAccount() {
    const body: ApiUserTokensAccountDeleteRequest = {
      id: 'delete',
      command: 'accountBackendUserTokens/delete'
    };
    return this.httpWithoutInterceptor.post<ApiUserTokensAccountDeleteResponse>(this.environmentService.environment.urlMainApi, JSON.stringify(body));
  }
}
